import Cookies from 'js-cookie'
import deepmerge from 'deepmerge'
import { v4 as uuidv4 } from 'uuid'
import { NECESSARY_CONSENT, NECESSARY_ESSENTIAL_CONSENT, PERSONALIZED_CONSENT } from '../../store/modules/cookies/config'
import logger from '../../utilities/logger'
import {
  appConfig as config, globalContext, trackingPageName, schemaMap
} from '../../store/modules/snowplow/config'
import $snowplowWrapperInit from './snowplow-wrapper'
import featuresConfig from '../../utilities/featureManager.features'
import getFeatures from '../../utilities/featureManager'
import { getCookieConsentStatus, isAnonymousTracking } from '../../utilities/cookieConsent'

/**
 * ### Snowplow analytics
 *
 * Vue Plugin implements Snowplow analytics
 *  - Namespace: `Snowplow`
 *  - Instantiates plugin via `snowplowInit`
 *    - Note: Initial `config` comes from `./deploy/configs/config.json`
 *    - `Snowplow.config.enabled` is set via JETFM `consumerhelpweb-snowplow-enabled` feat flag
 *      - if not `null` or `undefined` (or remains based on initial `config.json` value)
 *  - Loads the library wrapper script and instantiates JET Snowplow
 *    - CDN configuration for L-JE or OneWeb from JETFM `oneweb-rollout` feat flag
 *  - Public methods available through the Vue app
 *    - `$setGlobalContext`
 *    - `$getGlobalContext`
 *    - `$trackPageView`
 *    - `$trackSelfDescEvent`
 *  - On Snowplow unavailable, pushes pending events to `Snowplow.pendingEvents<Array>`
 *    - `Snowplow.pendingEvents` executes when Snowplow `window.analytics_pipeline` becomes available and reduces the list
 *  - Set `window.$spGlobalContext` needed by Automation component
 *
 * #### TBC:
 *  - Clarify behaviour based on Cookie consent
 *  - Improve test coverage
 *  - Set Global Context in Store for debug and visibility
 *    - Update state
 *
 */


/**
 * Define name space `Snowplow`
 * Initial plugin config and actions
 */
const Snowplow = {
  enabled: false,
  config,
  globalContext,
  pendingEvents: []
}

/**
 * Obtain app language
 * @param {String} tenant value
 * @returns Last two characters from a `locale` string e.g. `en-GB`
 * Future: Obtain from state locale where is possible to update
 */
const getLanguage = locale => (locale && locale.slice(-2).toLowerCase()) || ''

/**
 * Retrieve Global Context
 * @return {Array} global context values [{data, schema}]
 */
const getGlobalContext = () => Snowplow.globalContext.map(c => ({ data: c.data, schema: c.schema }))

/**
 * Add or Update to Global Context
 * @param {String} name of the context
 * @param {Object} data object of the context
 * @param {String} schema value of the context
 * @returns new context Object
 */
const setGlobalContext = (name, data, schema, mergeData = true) => {
  const index = Snowplow.globalContext.findIndex(c => c.name === name)
  let context = {}

  if (index === -1) {
    // Add new
    context = { name, data, schema }
    Snowplow.globalContext.push(context)
  } else {
    // Update existing
    const { data: currData, schema: currSchema } = Snowplow.globalContext[index]
    const newData = mergeData ? deepmerge(currData, data) : data
    const newSchema = (schema && schema.length > 0) ? schema : currSchema
    context = { name, data: newData, schema: newSchema }
    Snowplow.globalContext[index] = context
  }

  // Consumed by child components (e.g. Personalised Help)
  window.$spGlobalContext = getGlobalContext()
  return context
}

/**
 * Normalise cookie consent value against iglu schema
 * @param {Array} consent cookie consent value
 * @returns {Array} schema matching values
 */
const translateConsentToStatus = (consent = []) => consent.map(c => {
  if (c === NECESSARY_CONSENT) {
    return NECESSARY_ESSENTIAL_CONSENT
  }
  if (c === PERSONALIZED_CONSENT) {
    return 'personalised'
  }
  return c
})

/**
 * Snowplow track Page/Screen view
 *
 * Track pending events
 * From route destination Object
 *  - Update Global Context `cx_page` with
 *    - `id` -> UUID on each page load
 *    - `name` -> route name from VVue router
 *
 *  - Send tracking event with Global Contexts
 *  - Queues events in pending if SP not ready
 * @param {String} name Vue route name
 */
const trackPageView = name => {
  const {
    cookieName, enabled, oneWebFeat, tenant
  } = Snowplow.config
  if (oneWebFeat === undefined) return Snowplow.pendingEvents.push({ trackPageView: [name] })
  const cookieConsent = getCookieConsentStatus(cookieName, tenant)
  const anonymousTracking = isAnonymousTracking(cookieConsent, tenant)
  if (!anonymousTracking && enabled && window.analytics_pipeline) {
    setGlobalContext('cx_consent', { status: translateConsentToStatus(cookieConsent) }, null, false)
    const data = {
      id: uuidv4(),
      name: trackingPageName[name] || ''
    }
    // eslint-disable-next-line no-use-before-define
    trackPendingEvents()
    setGlobalContext('cx_page', data)

    return window.analytics_pipeline('trackPageView', {
      context: [
        ...getGlobalContext()
      ]
    })
  }
  return Snowplow.pendingEvents.push({ trackPageView: [name] })
}

/**
 * Send Snowplow self describing event
 * incl. event context and Global Context
 * @param {String} name *Required* Event name
 * @param {Array} eventContext optional
 * @param {Object} data optional Event data
 * @returns Snowplow tracking event
 *
 *  - Track pending events
 *  - Send tracking event with Global Contexts
 *  - Queues events in pending if SP not ready
 */
const trackSelfDescEvent = (name, eventContext = [], data = {}) => {
  if (name === undefined) return logger.warn('Analytics Self Describing Event Failed! Missing event `name`', { eventContext, data })
  const {
    cookieName, enabled, tenant
  } = Snowplow.config
  const cookieConsent = getCookieConsentStatus(cookieName, tenant)
  const anonymousTracking = isAnonymousTracking(cookieConsent, tenant)

  if (!anonymousTracking && enabled && window.analytics_pipeline) {
    setGlobalContext('cx_consent', { status: translateConsentToStatus(cookieConsent) }, null, false)
    // eslint-disable-next-line no-use-before-define
    trackPendingEvents()
    const eventGlobalContext = getGlobalContext()
    const context = [...eventContext, ...eventGlobalContext]
    const schema = schemaMap[name]

    if (schema === undefined) logger.warn('Self Describing Event Failed! Missing `schema`', { name, eventContext, data })

    // Send event
    return window.analytics_pipeline && window.analytics_pipeline('trackSelfDescribingEvent', {
      event: {
        data,
        schema
      },
      context
    })
  }

  return Snowplow.pendingEvents.push({ trackSelfDescEvent: [name, eventContext, data] })
}

/**
 * Track any pending events to be sent
 * e.g. following a Cookie consent selection
 * from Snowplow.pendingEvents
 */
const trackPendingEvents = () => {
  do {
    const pending = Snowplow.pendingEvents.shift()
    if (pending && Object.keys(pending).includes('trackPageView')) {
      const args = pending.trackPageView
      trackPageView.apply(this, args)
    }
    if (pending && Object.keys(pending).includes('trackSelfDescEvent')) {
      const args = pending.trackSelfDescEvent
      trackSelfDescEvent.apply(this, args)
    }
  } while (Snowplow.pendingEvents.length > 0)
}

const importWrapper = remoteConfig => {
  $snowplowWrapperInit && $snowplowWrapperInit(remoteConfig)
}

/**
 * Creates a new instance of Snowplow
 *  - Set Global Context Cookies consent values
 *  - Set Global Context platform data
 *  - Set Global Context User `anonymousId` from cookie
 *  - Instantiate tracking script
 *  - Track pending actions
 */
const snowplowInit = () => {
  const {
    cdnConfig, cdnConfigLegacy, cookieName, enabled, oneWebFeat, locale, tenant, userCookieName
  } = Snowplow.config

  const remoteConfig = oneWebFeat.value ? cdnConfig : cdnConfigLegacy
  window.analytics_pipeline === undefined && importWrapper(remoteConfig)

  const platformData = {
    language: getLanguage(locale),
    country: tenant
  }

  const cookieConsent = getCookieConsentStatus(cookieName, tenant)
  const anonymousTracking = isAnonymousTracking(cookieConsent, tenant)
  setGlobalContext('cx_consent', { status: translateConsentToStatus(cookieConsent) })

  setGlobalContext('cx_platform', platformData)

  const anonymousId = Cookies.get(userCookieName) || uuidv4()
  setGlobalContext('cx_user', { anonymousId })

  // Snowplow loaded
  if (!anonymousTracking && enabled && window.analytics_pipeline) {
    trackPendingEvents()
  }

  return Snowplow.config.enabled
}

Snowplow.install = (Vue, initConfig) => {
  Snowplow.config = deepmerge(Snowplow.config, initConfig)

  async function init() {
    const { snowplowToggle, onewebRollout } = featuresConfig
    const { snowplowFeat = snowplowToggle, oneWebFeat = onewebRollout } = await getFeatures({ snowplowToggle, onewebRollout })
    Snowplow.config.enabled = snowplowFeat.value
    Snowplow.config.oneWebFeat = oneWebFeat

    if (Snowplow.config.enabled) {
      snowplowInit()
    } else {
      logger.warn('>>> Snowplow disabled')
    }
  }
  (init())

  /**
   * Add/Update global context Object
   * @param {String} name of context
   * @param {Object} data props
   * @param {String} schema value
   * @param {Boolean} mergeData Merge data or overwrite, defaults to true
   */
  Vue.prototype.$setGlobalContext = (name, data, schema, mergeData) => {
    setGlobalContext(name, data, schema, mergeData)
  }

  /**
   * Return Snowplow Global context set on instantiation
   */
  Vue.prototype.$getGlobalContext = () => {
    getGlobalContext()
  }

  /**
   * Send analytics Page/Screen view event
   * @param {Object} id UUID v4 per page load
   * @param {Object} route from Vue Router
  */
  Vue.prototype.$trackPageView = route => {
    trackPageView(route)
  }

  /**
   * Track app `selfDescribingEvent`
   * Note: `eventName` needs to match `schemaMap`
   * @param {String} name based on `schemaMap` import
   * @param {Array} context any event specific context additional to the Global Contexts
   * @param {Object} data additional properties
   * https://pages.github.je-labs.com/analytics-platform/snowplow-documentation/docs/new-integrations/self-describing-events#required-contexts
   */
  Vue.prototype.$trackSelfDescEvent = (name, context, data) => {
    trackSelfDescEvent(name, context, data)
  }
}

export default Snowplow
